/**
 * PathMapper class.
 *
 * This class provides path mapping functionality for image and file assets.
 *
 * Also provides validation that a destination path is internal to Drupal.
 *
 * @type {{getHelpBubbleIconPath(*=): (*|string)}}
 */
const PathMapper = {
  /**
   * Get the path for an icon asset.
   *
   * @param {string} icon - String representing the name of an icon asset to load.
   * @param {Boolean} isB4 - flag to determine whether or not the context is BioRAFT4.
   *
   * @return {string} - Returns a string representing the path to the icon.
   */
  getIconAssetPath(icon, isB4) {
    let map = {};

    if (isB4) {
      map = this.b4IconAssetMap();
    } else {
      map = this.b3IconAssetMap();
    }

    if (typeof map[icon] === "undefined") {
      icon = "druplicon.png";
    }

    return map[icon] + icon;
  },

  /**
   * The B3 icon asset map.
   *
   * @return {Object}
   *   Returns an object with icon names as the keys and and corresponding
   *   BioRAFT3 filepaths as the values.
   */
  b3IconAssetMap() {
    return {
      "help.png": "/sites/all/modules/RAFT/raft_crm/images/icons/",
      "download.png": "/sites/all/modules/RAFT/raft_crm/images/icons/",
      "csv.png": "/sites/all/modules/viewsModules/views_bonus/",
      "druplicon.png": "/misc/",
      "images.png":
        "/sites/all/modules/RAFT/raft_safety_observation/images/icons/",
      "email.png": "/sites/all/modules/RAFT/raft_crm/images/icons/",
      "email-disabled.png": "/sites/all/modules/RAFT/raft_crm/images/icons/",
      "magnifier.png": "/sites/all/modules/RAFT/raft_crm/images/icons/",
      "exclamation.png": "/sites/all/modules/RAFT/raft_crm/images/icons/",
      "throbber.gif":
        "/sites/all/modules/RAFT/bioraft4/modules/chemdb/css/images/throbber.gif",
      "upload.png": "/sites/all/modules/RAFT/raft_crm/images/icons/",
      "Message.svg": "/sites/all/modules/RAFT/raft_crm/images/icons/",
      "CSV.svg": "/sites/all/modules/RAFT/raft_crm/images/icons/",
      "QR.svg": "/sites/all/modules/RAFT/raft_crm/images/icons/",
    };
  },

  /**
   * The B4 icon asset map.
   *
   * @return {Object}
   *   Returns an object with icon names as the keys and and corresponding
   *   BioRAFT4 filepaths as the values.
   */
  b4IconAssetMap() {
    return {
      "help.png": "/core/misc/",
      "csv.png": "/assets/icons/",
      "druplicon.png": "/core/misc/",
      "images.png": "/assets/icons/",
      "email.png": "/assets/icons/",
      "email-disabled.png": "/assets/icons/",
      "magnifier.png": "/assets/icons/",
      "download.png": "/assets/icons/",
      "exclamation.png": "/assets/icons/",
      "upload.png": "/assets/icons/",
      "Message.svg": "/assets/icons/",
      "CSV.svg": "/assets/icons/",
      "QR.svg": "/assets/icons/",
    };
  },

  /**
   * Returns the passed in URL as an absolute URL.
   *
   * @param {string} url
   *   The URL string to be normalized to an absolute URL.
   *
   * @return {string}
   *   The normalized, absolute URL.
   *
   * @see https://github.com/angular/angular.js/blob/v1.4.4/src/ng/urlUtils.js
   * @see https://grack.com/blog/2009/11/17/absolutizing-url-in-javascript
   * @see https://github.com/jquery/jquery-ui/blob/1.11.4/ui/tabs.js#L53
   */
  toAbsolute(url) {
    const urlParsingNode = document.createElement("a");

    // Decode the URL first; this is required by IE <= 6. Decoding non-UTF-8
    // strings may throw an exception.
    try {
      url = decodeURIComponent(url);
    } catch (e) {
      // Empty.
    }

    urlParsingNode.setAttribute("href", url);

    // IE <= 7 normalizes the URL when assigned to the anchor node similar to
    // the other browsers.
    return urlParsingNode.cloneNode(false).href;
  },

  /**
   * Returns true if the URL is within Drupal's base path.
   *
   * @see Drupal.url.isLocal in drupal.es6.js.
   *
   * @param {string} url
   *   The URL string to be tested.
   *
   * @return {boolean}
   *   `true` if local.
   *
   * @see https://github.com/jquery/jquery-ui/blob/1.11.4/ui/tabs.js#L58
   */
  isLocal(url) {
    // Always use browser-derived absolute URLs in the comparison, to avoid
    // attempts to break out of the base path using directory traversal.
    let absoluteUrl = this.toAbsolute(url);
    let protocol = window.location.protocol;

    // Consider URLs that match this site's base URL but use HTTPS instead of HTTP
    // as local as well.
    if (protocol === "http:" && absoluteUrl.indexOf("https:") === 0) {
      protocol = "https:";
    }
    // Assume BioRAFT3 and BioRAFT4 are never installed in a subdirectory.
    let baseUrl = protocol + "//" + window.location.host;

    // Decoding non-UTF-8 strings may throw an exception.
    try {
      absoluteUrl = decodeURIComponent(absoluteUrl);
    } catch (e) {
      // Empty.
    }
    try {
      baseUrl = decodeURIComponent(baseUrl);
    } catch (e) {
      // Empty.
    }

    // The given URL matches the site's base URL, or has a path under the site's
    // base URL.
    return absoluteUrl === baseUrl || absoluteUrl.indexOf(baseUrl + "/") === 0;
  },
};

export default PathMapper;
